import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Input, Textarea } from "@/components/ui/Input";
import { FilingTypeField } from "./FilingTypeField";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { PropertyInfoForm } from "@/features/upload/property/PropertyInfoForm";
import { usePropertyInfo } from "@/features/upload/property/usePropertyInfo";
import { createDocument, updateDocument } from "@/features/history/api";
import {
  annotatePaaStreaming,
  extractPaaPdfStreaming,
  generatePaaPdf,
  recomposePaa,
  recordNoteEdit,
  reportSheetProblem,
  releasePaaCompare,
  savePropertyInfo,
  startSession,
  endSession,
  PaaCompareExpiredError,
  type PaaCompareContextInfo,
  type PaaOldSheet,
  type PaaRowNotePatch,
  type PropertyInfoDto,
} from "./api";
import {
  formatRevision,
  isChangedVerdict,
  makeEmptyRow,
  pageIdsFor,
  parseRevisionMinor,
  type PaaAction,
  type PaaExtraction,
  type PaaRow,
} from "./types";
import {
  indexOfRow,
  nextOutstanding,
  reviewProgress,
  reviewQueue,
  reviewedAfterNote,
  setReviewed,
  step as stepSheet,
} from "./review/queue";
import { CONFIRM_ABOVE_USD, EST_USD_PER_SHEET, aiSheetCount, willAiCompare } from "./compareCost";
import { CompareConfirmDialog } from "./CompareConfirmDialog";
import { CompareOverlay } from "./CompareOverlay";
import {
  WhatChangedPanel,
  type CompareState,
  type CompareTally,
} from "./WhatChangedPanel";
import { SheetCompareViewer } from "./SheetCompareViewer";
import { SheetFindings, type ReportKind } from "./SheetFindings";
import { useWhatChangedEnabled } from "./flags";
import { usePlanPdfDrop } from "./usePlanPdfDrop";
import { CombineStep } from "./combine/CombineStep";
import {
  buildPaaCombine,
  preparePaaCombine,
  releasePaaCombine,
} from "./combine/api";
import type {
  CombineMove,
  CombinePageRef,
  CombinePlan,
  CombineRow,
} from "./combine/types";
import { toRows } from "./combine/plan";
import {
  approvedFromCombine,
  approvedFromCompare,
  pairingFor,
  pairingProgress,
  setPairing,
  settleAllSameNumber,
  settleSameNumber,
  unsettleSameNumber,
  type ApprovedSheet,
} from "./review/pairing";
import {
  PairingChoice,
  SameNumberBanner,
  SameNumberPrompt,
} from "./review/SheetPairing";
import { ApiError } from "@/lib/api";

type Step =
  | "upload"
  | "processing"
  | "review"
  | "generating"
  | "download"
  | "combine";

// The "what changed" comparison is a first-class review-page activity: idle
// offers to add the approved set, running streams per-sheet notes into the
// table live, done/error summarize the last run (with a re-compare / retry).
// Staging the approved set on the upload page just auto-starts this same run
// the moment review opens — one code path either way. CompareState itself
// lives with the panel that renders it.

const MAX_BYTES = 100 * 1024 * 1024;

// Whether this run should describe what changed, asked as ONE checkbox beside
// the approved set on the upload page.
//
// Why it is asked at all: that file used to mean exactly one thing, so dropping
// it started an AI pass over every revised sheet. It now has a second, free use
// — building the combined plan set — and an operator who brought it in for that
// was billed for the first and made to wait minutes for it. A file is not an
// instruction.
//
// Why it is CHECKED: notes are what the office normally wants, and the
// alternative is a click on every filing to get today's behaviour. The unwanted
// case is one untick, next to the file, with the cost written beside it.
//
// It briefly moved to a pairing step in front of review, on the argument that a
// decision is cheapest where its inputs exist. What that actually bought was a
// compulsory walk through every page before anything could be described, which
// is the opposite of what was asked for. The walk is still here — it is the
// drawing surface, opened from the row — and the question is back beside the
// file it is about.
const NOTES_BY_DEFAULT = true;

interface ConfigDto {
  googleMapsApiKey: string | null;
}

export function PaaPage() {
  const [step, setStep] = useState<Step>("upload");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<PaaExtraction | null>(null);
  const [progress, setProgress] = useState<string[]>([]);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [oldFileName, setOldFileName] = useState<string | null>(null);
  const oldFileRef = useRef<File | null>(null);
  // The processed (updated) plan File, kept through review so the comparison
  // can run — or re-run — at any point without leaving the page.
  const newFileRef = useRef<File | null>(null);
  const [compare, setCompare] = useState<CompareState>({ status: "idle" });
  // Row ids whose note is still being computed by a running comparison —
  // those rows show a small "comparing…" placeholder in the table.
  const [pendingNoteIds, setPendingNoteIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const compareAbortRef = useRef<AbortController | null>(null);
  // Cost-confirm popup for large runs. The dialog is shown by setting this state;
  // the resolver lets startCompare await the user's Continue/Cancel choice.
  const [compareConfirm, setCompareConfirm] = useState<{
    sheets: number;
    estimateUsd: string;
  } | null>(null);
  const compareConfirmResolve = useRef<((ok: boolean) => void) | null>(null);
  // The approved set's sheet list from the last comparison — feeds the baseline
  // dropdowns. Also carries the compareId: while the server still holds that
  // session, a rewrite or a per-row re-compare skips both uploads and the
  // re-index. It expires, so both Files stay in refs as the fallback.
  const [compareCtx, setCompareCtx] = useState<PaaCompareContextInfo | null>(null);
  const compareIdRef = useRef<string | null>(null);
  // ---- Combining the two plan sets: the proposed order under review, the id the
  // server parked both PDFs under, and the built file. A side trip off review or
  // download, so it keeps its own state and its own way back.
  const [combinePlan, setCombinePlan] = useState<CombinePlan | null>(null);
  const [combineId, setCombineId] = useState<string | null>(null);
  const combineIdRef = useRef<string | null>(null);
  const [combineBusy, setCombineBusy] = useState(false);
  const [combineProgress, setCombineProgress] = useState<string | null>(null);
  const [combineFile, setCombineFile] = useState<{ url: string; name: string } | null>(null);
  // The order under review. Here rather than in the step, so a trip back to the
  // download screen and in again does not throw away a reordering.
  const [combineRows, setCombineRows] = useState<CombineRow[]>([]);
  const combineReturnRef = useRef<Step>("review");
  // The rows the current plan was computed from. Anything that would change the
  // plan changes this, and only then is the approved set read again.
  const combineRowsSigRef = useRef<string | null>(null);
  // The stats session outlives itself for one purpose: generate ENDS the session
  // and clears the id, and combining happens after that. What the operator moves
  // in the combined set still belongs to this run.
  const combineSessionIdRef = useRef<string | null>(null);
  // The gist or the opening line edited since the notes were last written — the
  // notes on the form still tell the OLD story until a rewrite runs.
  const [storyDirty, setStoryDirty] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  // Row whose approved/updated drawings are open side by side, if any.
  // Which sheet the drawing surface is open on. Held as a row for the call
  // sites' convenience, but ALWAYS re-read from `data` before use: the note is
  // edited on that surface now, and rendering the snapshot captured when it
  // opened would show her a stale sentence and silently discard her typing.
  const [viewingSheet, setViewingSheet] = useState<PaaRow | null>(null);
  // Rows whose note the USER typed (this run) — a comparison never overwrites
  // these; it reports how many it kept instead.
  const userNoteIdsRef = useRef<Set<string>>(new Set());
  // Mirror of `data` for callbacks that need the current value (startCompare
  // snapshots the rows at the moment the comparison begins).
  const dataRef = useRef<PaaExtraction | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Design intent staged on the upload page, before any extraction exists to
  // hold it.
  const intentRef = useRef<string>("");
  // Whether the comparison runs when review opens — see NOTES_BY_DEFAULT.
  const [wantsNotes, setWantsNotes] = useState(NOTES_BY_DEFAULT);
  const wantsNotesRef = useRef(wantsNotes);
  const abortRef = useRef<AbortController | null>(null);
  // Stats session id for this run, so PAA usage shows on the dashboard.
  const sessionIdRef = useRef<string | null>(null);
  // Archive row for this run — created right after extraction, updated
  // (debounced) as the user edits, finalized at generate.
  const documentIdRef = useRef<string | null>(null);
  const lastSyncedDataRef = useRef<PaaExtraction | null>(null);

  const configQuery = useQuery<ConfigDto>({
    queryKey: ["config"],
    queryFn: () => api<ConfigDto>("/api/config"),
    staleTime: Infinity,
  });
  const googleMapsApiKey = configQuery.data?.googleMapsApiKey ?? null;

  // Address verification + saved-property resolution — shared with the objection
  // flow. Driven by a merge-patch so it works with this page's useState.
  const propertyInfo = usePropertyInfo({
    active: step === "review",
    data,
    patch: (p) => setData((d) => (d ? { ...d, ...p } : d)),
    googleMapsApiKey,
  });

  const pushProgress = useCallback((msg: string) => {
    const ts = new Date().toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    setProgress((prev) => [...prev, `${ts}  ${msg}`]);
  }, []);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    wantsNotesRef.current = wantsNotes;
  }, [wantsNotes]);

  // Apply one server note patch to its row (used by both the streaming run
  // and the per-row re-compare).
  const applyRowNotePatch = useCallback((patch: PaaRowNotePatch) => {
    setData((d) =>
      d
        ? {
            ...d,
            rows: d.rows.map((r) =>
              r.id === patch.id
                ? {
                    ...r,
                    // Her confirmation was of a specific sentence. If the engine
                    // has just written a different one she has not seen it, so
                    // the tick goes — an unchanged note keeps it, or re-running
                    // a comparison would wipe an afternoon of review.
                    reviewed: reviewedAfterNote(r, patch.changeNote),
                    changeNote: patch.changeNote,
                    // The engine's original, kept verbatim — generate compares
                    // it against whatever the user ends up filing.
                    aiChangeNote: patch.aiChangeNote,
                    oldSheetMissing: patch.oldSheetMissing,
                    comparedTo: patch.comparedTo,
                    verdict: patch.verdict,
                    verdictExplanation: patch.verdictExplanation,
                    revisionLooksStale: patch.revisionLooksStale,
                    needsReview: patch.needsReview,
                    reviewReason: patch.reviewReason,
                    readSummary: patch.readSummary,
                    unexplainedAreas: patch.unexplainedAreas,
                    readOnOneSide: patch.readOnOneSide,
                    blindSpot: patch.blindSpot,
                  }
                : r,
            ),
          }
        : d,
    );
  }, []);

  // ---------------------------------------------------------------------
  // The review pass
  //
  // She has to look at every page. What she has never had is anywhere that
  // remembers which ones — so the only way to be sure was to scan the table
  // again. This drives the drawing surface as a sequence: one sheet, both
  // drawings, the sentence, confirm, next.
  // ---------------------------------------------------------------------

  // The live row, never the snapshot taken when the surface opened.
  const viewingRow = useMemo(
    () =>
      viewingSheet ? (data?.rows.find((r) => r.id === viewingSheet.id) ?? null) : null,
    [viewingSheet, data],
  );

  const patchRow = useCallback((id: string, p: Partial<PaaRow>) => {
    // A note she typed must survive any later comparison — same bookkeeping the
    // table's own editor does.
    if ("changeNote" in p) userNoteIdsRef.current.add(id);
    setData((d) =>
      d ? { ...d, rows: d.rows.map((r) => (r.id === id ? applyRowPatch(r, p) : r)) } : d,
    );
  }, []);

  // The approved set's sheets, for the pairing control on the row and on the
  // drawing surface.
  //
  // Two things read that set and BOTH are the same server-side scan, so either
  // answers this: the comparison's `context` event and the combine plan's
  // approved index. The comparison is preferred when one is live because it is
  // the index its notes were actually written against — showing a choice the
  // last run could not have used would be a screen arguing with itself.
  const approvedSheets = useMemo<ApprovedSheet[]>(() => {
    const fromCompare = approvedFromCompare(compareCtx?.oldSheets);
    return fromCompare.length > 0
      ? fromCompare
      : approvedFromCombine(combinePlan?.approvedSheets);
  }, [compareCtx, combinePlan]);

  /** Change what a row supersedes, from either surface. Goes through the
   *  pairing helpers rather than a raw patch: the answer also drops the row's
   *  confirmation, and a second copy of that rule would eventually forget to. */
  const repair = useCallback((next: (rows: PaaRow[]) => PaaRow[]) => {
    setData((d) => (d ? { ...d, rows: next(d.rows) } : d));
  }, []);

  /** Open the pass on the first sheet still outstanding, or the first sheet if
   *  everything is already confirmed (she may just want another look). */
  const startReviewPass = useCallback(() => {
    if (!data) return;
    const queue = reviewQueue(data.rows);
    if (queue.length === 0) return;
    setViewingSheet(nextOutstanding(data.rows, null) ?? queue[0]);
  }, [data]);


  // Show the cost-confirm popup and resolve with the user's choice. Awaited by
  // startCompare for large runs.
  const askCompareConfirm = useCallback(
    (sheets: number, estimateUsd: string) =>
      new Promise<boolean>((resolve) => {
        compareConfirmResolve.current = resolve;
        setCompareConfirm({ sheets, estimateUsd });
      }),
    [],
  );
  const settleCompareConfirm = useCallback((ok: boolean) => {
    setCompareConfirm(null);
    compareConfirmResolve.current?.(ok);
    compareConfirmResolve.current = null;
  }, []);

  // ---------------------------------------------------------------------------
  // "What changed" comparison — runs on the review page, whether the approved
  // set was staged on the upload page (auto-started after extraction) or added
  // here. Streams per-sheet notes into the table as they land; the review stays
  // fully editable throughout. Notes the user typed are never overwritten.
  // `rowIds` restricts the run (per-row re-compare fallback when the cached
  // context expired) — those rows ARE overwritten, since the re-compare is
  // explicit.
  // ---------------------------------------------------------------------------
  const startCompare = useCallback(
    async (oldFile: File, snapshot?: PaaExtraction, rowIds?: string[]) => {
      const planFile = newFileRef.current;
      const current = snapshot ?? dataRef.current;
      if (!planFile || !current) {
        toast.error("The processed plan is no longer available — start over to compare.");
        return;
      }
      const targetRows = rowIds
        ? current.rows.filter((r) => rowIds.includes(r.id))
        : current.rows;
      if (!targetRows.some((r) => r.sheetId.trim().length > 0)) {
        toast.info("Add at least one sheet row before comparing.");
        return;
      }

      // Cost guard for full runs: each AI'd sheet bills the client's OpenAI key.
      // Only confirm when the estimated cost crosses the threshold. A per-row
      // re-compare (rowIds) is one sheet by definition — never gated.
      if (!rowIds) {
        const sheets = aiSheetCount(targetRows, userNoteIdsRef.current);
        const estUsd = sheets * EST_USD_PER_SHEET;
        if (estUsd > CONFIRM_ABOVE_USD) {
          const ok = await askCompareConfirm(sheets, estUsd.toFixed(2));
          if (!ok) return;
        }
      }

      compareAbortRef.current?.abort();
      const controller = new AbortController();
      compareAbortRef.current = controller;

      // Remember the approved set so per-row re-compares can fall back to a
      // full restricted run after the server cache expires.
      oldFileRef.current = oldFile;
      setOldFileName(oldFile.name);

      // An explicit (restricted) re-compare overwrites those rows by intent.
      if (rowIds) rowIds.forEach((id) => userNoteIdsRef.current.delete(id));

      // Only rows that actually get compared show the "comparing…" indicator —
      // "N"/"O" rows resolve to a stock note instantly, so they never spin.
      const pendingIds = targetRows
        .filter((r) => willAiCompare(r, userNoteIdsRef.current))
        .map((r) => r.id);
      setPendingNoteIds(new Set(pendingIds));
      setCompare({
        status: "running",
        fileName: oldFile.name,
        message: "Uploading both plan sets…",
        // The server annotates (and reports progress for) every sheet-id row,
        // so the overlay's denominator must match that — not the AI-only subset.
        total: targetRows.filter((r) => r.sheetId.trim().length > 0).length,
        done: 0,
        rowScoped: !!rowIds,
      });

      // See the compareId note below: a full run supersedes whatever session we
      // were holding, and the server has no way to work that out for itself.
      if (!rowIds) {
        releasePaaCompare(compareIdRef.current);
        compareIdRef.current = null;
      }

      let applied = 0;
      let skipped = 0;
      try {
        await annotatePaaStreaming(planFile, oldFile, current, {
          sessionId: sessionIdRef.current,
          signal: controller.signal,
          rowIds,
          // Only a SCOPED re-compare may reuse the indexed sets: a full run is
          // either the first one or an explicit "compare a different set", and
          // both must index for real.
          //
          // Which is exactly why a full run must RELEASE the previous session
          // first. Because we send null here, the server cannot tell it has been
          // superseded, so without this the old pair of indexed plan sets stays
          // pinned for its full 30-minute idle window while the new pair is being
          // read — two comparisons' worth of memory on a box that has room for
          // about one. "Compare a different approved set" is a normal thing to do
          // twice in a review, so this is not an edge case.
          compareId: rowIds ? compareIdRef.current : null,
          onContext: (ctx) => {
            compareIdRef.current = ctx.compareId;
            setCompareCtx(ctx);
          },
          // Persist the derived drivers on the extraction: the next annotate
          // call (e.g. a per-row re-compare) sends them back so the same
          // amendment story — same ids, same phrasing — is reused.
          onDrivers: (drivers) => setData((d) => (d ? { ...d, drivers } : d)),
          // The amendment in a paragraph. Held on the extraction so a rewrite
          // or a per-row re-compare sends it back and the model keeps the same
          // account of the filing instead of restating it differently.
          onGist: (gist) =>
            setData((d) => (d && !d.gistUserDefined ? { ...d, gist } : d)),
          onProgress: (msg) =>
            setCompare((c) =>
              c.status === "running" ? { ...c, message: msg } : c,
            ),
          // Drive the counter off the server's typed per-sheet event — it ticks
          // once per sheet as each is compared. The final notes for changed
          // sheets batch in at the end (Pass 2), so counting `row` events
          // would freeze the bar until that burst.
          onSheetProgress: (done, total) =>
            setCompare((c) =>
              c.status === "running"
                ? { ...c, done: Math.max(c.done, done), total }
                : c,
            ),
          onRow: (patch) => {
            setPendingNoteIds((prev) => {
              if (!prev.has(patch.id)) return prev;
              const next = new Set(prev);
              next.delete(patch.id);
              return next;
            });
            if (userNoteIdsRef.current.has(patch.id)) {
              skipped++;
              return;
            }
            applied++;
            applyRowNotePatch(patch);
          },
        });
        setCompare({
          status: "done",
          fileName: oldFile.name,
          noteCount: applied,
          skippedCount: skipped,
        });
        // The notes on the form now match the drivers that produced them.
        setStoryDirty(false);
        toast.success(
          skipped > 0
            ? `Wrote ${applied} change note${applied === 1 ? "" : "s"} (kept ${skipped} you wrote).`
            : `Wrote ${applied} change note${applied === 1 ? "" : "s"}.`,
        );
      } catch (err) {
        if (controller.signal.aborted) {
          setCompare({ status: "idle" });
          toast.info("Comparison cancelled");
        } else {
          setCompare({
            status: "error",
            fileName: oldFile.name,
            message: (err as Error).message,
          });
          toast.error(`Comparison failed: ${(err as Error).message}`);
        }
      } finally {
        setPendingNoteIds(new Set());
        if (compareAbortRef.current === controller) compareAbortRef.current = null;
      }
    },
    [applyRowNotePatch, askCompareConfirm],
  );

  // ---------------------------------------------------------------------------
  // Per-row re-compare — honors the row's baseline override (compareWith) and
  // AI hint. Re-runs the comparison scoped to this one row (re-uploads + re-
  // reads both sets). It's a rare, explicit action, so it's not worth keeping
  // the indexed plan sets in server memory between requests; startCompare
  // handles the pending/overwrite bookkeeping.
  // ---------------------------------------------------------------------------
  const recompareRowById = useCallback(
    async (rowId: string) => {
      const current = dataRef.current;
      const row = current?.rows.find((r) => r.id === rowId);
      if (!current || !row) return;
      if (!row.sheetId.trim()) {
        toast.info("Enter a Sheet ID first.");
        return;
      }
      const oldFile = oldFileRef.current;
      if (!oldFile) {
        toast.error("The approved set is no longer available — run the comparison again.");
        return;
      }
      await startCompare(oldFile, current, [rowId]);
    },
    [startCompare],
  );

  // ---------------------------------------------------------------------------
  // Driver review. Editing a driver, removing one, adding one the operator knows
  // about, or changing the design intent does NOT touch the form — it only marks
  // the notes stale. "Rewrite" is the commit, and because the server still holds
  // the indexed sets plus every sheet's recorded facts, it runs the composition
  // pass alone: one AI call instead of the whole comparison. That cost is the
  // whole reason this can be an interactive loop.
  // ---------------------------------------------------------------------------
  // A note field was committed. Sent from the CURRENT data via a ref so the
  // handler identity stays stable across every keystroke in the table — this
  // fires on blur, and re-rendering thirty rows to keep a closure fresh would
  // cost more than the capture it enables.
  //
  // Fire-and-forget: the server dedups against the sheet's last recorded
  // version, so a blur with no change writes nothing and a double-fire is
  // harmless.
  const onNoteCommitted = useCallback((rowId: string) => {
    const current = dataRef.current;
    if (current) recordNoteEdit(current, rowId, sessionIdRef.current);
  }, []);

  const reviewSequence = useMemo(() => {
    if (!viewingRow || !data) return undefined;

    const rows = data.rows;
    const progress = reviewProgress(rows);
    const prev = stepSheet(rows, viewingRow.id, -1);
    const next = stepSheet(rows, viewingRow.id, 1);

    return {
      index: indexOfRow(rows, viewingRow.id) + 1,
      total: progress.total,
      reviewed: !!viewingRow.reviewed,
      remaining: progress.remaining,
      onPrevSheet: prev ? () => setViewingSheet(prev) : null,
      onNextSheet: next ? () => setViewingSheet(next) : null,
      onConfirm: () => {
        // Compute the next sheet from the rows as they will be AFTER this
        // confirmation, or the sheet just confirmed is still outstanding and
        // Confirm lands back on it.
        const confirmed = setReviewed(rows, viewingRow.id, true);
        setData((d) => (d ? { ...d, rows: confirmed } : d));
        // Null closes the surface, which is the honest signal that the pass is
        // finished — there is no next sheet to show.
        setViewingSheet(nextOutstanding(confirmed, viewingRow.id));
      },
      onNoteChange: (note: string) => patchRow(viewingRow.id, { changeNote: note }),
      onNoteCommitted: () => onNoteCommitted(viewingRow.id),
    };
  }, [viewingRow, data, patchRow, onNoteCommitted]);

  // She is telling us a reading was WRONG, or that we missed something — which
  // no note edit can express. Unlike the edit above this is awaited: she typed a
  // sentence and is owed an answer, and a report that vanishes quietly teaches
  // her not to bother.
  //
  // compareId decides whether the drawings are kept. The server renders both
  // sheets at report time because the comparison holding the plan sets expires
  // and nothing else stores them; after that the report stands without its
  // evidence, which the panel says out loud.
  //
  // The id of what she already sent for a row is remembered here, so a second
  // send EDITS that report instead of filing a near-duplicate. In a ref rather
  // than state for the same reason as the note capture above: nothing renders
  // from it, and re-rendering thirty rows to keep a closure fresh would cost
  // more than the capture it enables.
  const reportIdsRef = useRef(new Map<string, string>());
  const onReportRow = useCallback(
    async (rowId: string, kind: ReportKind, message: string) => {
      const current = dataRef.current;
      if (!current) throw new Error("There is nothing to report against yet.");
      const result = await reportSheetProblem(
        current,
        rowId,
        sessionIdRef.current,
        compareIdRef.current,
        kind,
        message,
        reportIdsRef.current.get(rowId) ?? null,
      );
      reportIdsRef.current.set(rowId, result.id);
    },
    [],
  );

  // Editing the gist claims it: a later rewrite reads the operator's words as
  // the authoritative account of the filing, and never replaces them with its
  // own. A field that silently reverts is a field nobody edits twice.
  const setGist = useCallback((gist: string) => {
    setData((d) => (d ? { ...d, gist, gistUserDefined: true } : d));
    setStoryDirty(true);
  }, []);

  const setDesignIntent = useCallback((designIntent: string) => {
    setData((d) => (d ? { ...d, designIntent } : d));
    setStoryDirty(true);
  }, []);

  // Rows a rewrite would touch: genuinely compared sheets whose note the user
  // hasn't taken over. Mirrors what the server will actually rewrite, so the
  // button never promises more than it delivers.
  const rewritableRows = useCallback(
    (d: PaaExtraction | null) =>
      (d?.rows ?? []).filter(
        (r) =>
          !!r.comparedTo &&
          !!r.changeNote?.trim() &&
          !userNoteIdsRef.current.has(r.id),
      ),
    [],
  );

  const handleRewrite = useCallback(async () => {
    const current = dataRef.current;
    const compareId = compareIdRef.current;
    if (!current || rewriting) return;

    // No live session: the facts are gone, so the only honest way to re-write
    // the notes is a full comparison. We still hold the approved set.
    const fallbackToFullRun = () => {
      const oldFile = oldFileRef.current;
      if (!oldFile) {
        toast.error("The approved set is no longer available — add it again to rewrite the notes.");
        return;
      }
      compareIdRef.current = null;
      toast.info("Re-running the comparison to apply the new reasons.");
      void startCompare(oldFile, dataRef.current ?? current);
    };

    if (!compareId) {
      fallbackToFullRun();
      return;
    }

    setRewriting(true);
    try {
      await recomposePaa(compareId, current, {
        sessionId: sessionIdRef.current,
        skipRowIds: [...userNoteIdsRef.current],
        onProgress: () => {},
        onRow: applyRowNotePatch,
        onDrivers: (drivers) => setData((d) => (d ? { ...d, drivers } : d)),
        onGist: (gist) =>
          setData((d) => (d && !d.gistUserDefined ? { ...d, gist } : d)),
      });
      setStoryDirty(false);
      toast.success("Notes rewritten to match your summary.");
    } catch (err) {
      if (err instanceof PaaCompareExpiredError) {
        fallbackToFullRun();
      } else {
        toast.error(`Rewrite failed: ${(err as Error).message}`);
      }
    } finally {
      setRewriting(false);
    }
  }, [applyRowNotePatch, rewriting, startCompare]);

  // ---------------------------------------------------------------------------
  // Upload -> stream extract (auto-parse on drop, with live progress).
  // ---------------------------------------------------------------------------
  const handleFile = useCallback(
    async (file: File) => {
      if (file.size > MAX_BYTES) {
        toast.error(`File '${file.name}' exceeds the 100MB limit.`);
        return;
      }
      if (!file.name.toLowerCase().endsWith(".pdf")) {
        toast.error("Please upload a PDF plan document.");
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      newFileRef.current = file;
      userNoteIdsRef.current = new Set();
      setCompare({ status: "idle" });
      setCompareCtx(null);
      compareIdRef.current = null;
      setStoryDirty(false);
      setViewingSheet(null);
      setPendingNoteIds(new Set());
      setProgress([]);
      setStep("processing");
      setBusy(true);
      pushProgress(`Uploading ${file.name}…`);

      // Open a stats session so this PAA run shows on the dashboard. Non-fatal.
      sessionIdRef.current = null;
      try {
        sessionIdRef.current = await startSession("PAA", file.name);
      } catch {
        /* stats are best-effort */
      }

      try {
        const extracted = await extractPaaPdfStreaming(file, {
          onProgress: pushProgress,
          signal: controller.signal,
          sessionId: sessionIdRef.current,
        });
        const rows = (extracted.rows ?? []).map((r) => ({
          ...r,
          id: r.id || makeEmptyRow().id,
        }));
        if (rows.length === 0) {
          rows.push(makeEmptyRow());
          toast.info("No sheet revisions detected — add rows manually.");
        } else {
          toast.success(
            `Detected ${rows.length} sheet revision${rows.length === 1 ? "" : "s"}.`,
          );
        }
        propertyInfo.reset();
        // Intent typed on the upload page rides into the run — it is the
        // authoritative seed for driver derivation, so it has to be present
        // BEFORE the auto-started comparison, not added afterwards.
        const fresh: PaaExtraction = {
          ...extracted,
          rows,
          designIntent: intentRef.current.trim() || null,
        };
        setData(fresh);
        setStep("review");

        // The comparison runs because it was ASKED for on the upload page — not
        // because an approved set happens to be in hand. Same convenience as
        // before for anyone who wants notes (review opens and they stream in),
        // and no spend at all for anyone who brought the file to combine the
        // plan sets. When it was not asked for, the "what changed" panel on the
        // review page offers it with the sheet count and the estimate on the
        // button, so changing your mind costs one click and no surprise.
        if (
          wantsNotesRef.current
          && oldFileRef.current
          && rows.some((r) => r.sheetId.trim().length > 0)
        ) {
          void startCompare(oldFileRef.current, fresh);
        }

        // Persist the run immediately — the draft lands in History as soon
        // as extraction finishes. Best-effort: the flow works the same if
        // the save fails.
        documentIdRef.current = null;
        try {
          documentIdRef.current = await createDocument(
            "Paa",
            fresh,
            sessionIdRef.current,
          );
          lastSyncedDataRef.current = fresh;
        } catch {
          /* non-fatal */
        }
      } catch (err) {
        const aborted = controller.signal.aborted;
        if (aborted) {
          toast.info("Cancelled");
        } else {
          toast.error(`Extraction failed: ${(err as Error).message}`);
        }
        // Close the session as cancelled/errored so it isn't left "Incomplete".
        if (sessionIdRef.current) {
          endSession(sessionIdRef.current, {
            totalObjections: 0,
            status: aborted ? "Cancelled" : "Error",
            errorMessage: aborted ? "Cancelled" : (err as Error).message,
          }).catch(() => {});
          sessionIdRef.current = null;
        }
        setStep("upload");
      } finally {
        setBusy(false);
        abortRef.current = null;
      }
    },
    [pushProgress, startCompare],
  );

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  // ---------------------------------------------------------------------------
  // Keep the archived draft in sync with manual edits in review. Debounced;
  // the ref skips redundant writes when the data object hasn't changed.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (step !== "review" || !documentIdRef.current || !data) return;
    if (lastSyncedDataRef.current === data) return;
    const docId = documentIdRef.current;
    const t = window.setTimeout(() => {
      // Guard against a reset / new run that swapped (or cleared) the draft id
      // inside the debounce window — never write this edit to the wrong doc.
      if (documentIdRef.current !== docId) return;
      lastSyncedDataRef.current = data;
      updateDocument("Paa", docId, data).catch(() => {
        // Retry on the next edit — generate persists the final state anyway.
        lastSyncedDataRef.current = null;
      });
    }, 1200);
    return () => window.clearTimeout(t);
  }, [step, data]);

  // ---------------------------------------------------------------------------
  // Generate — save property info first (so future uploads autofill), then PDF.
  // ---------------------------------------------------------------------------
  const handleGenerate = useCallback(async () => {
    // A running comparison would race the generated PDF — the button is
    // disabled while it runs; this guard covers the keyboard/edge cases.
    if (!data || compareAbortRef.current) return;
    setStep("generating");
    setBusy(true);
    try {
      if (data.address?.trim()) {
        try {
          await savePropertyInfo({
            address: data.address,
            cbNumber: data.cbNumber,
            bin: data.bin,
            applicantName: data.applicantName,
            borough: data.borough,
            block: data.block,
            lot: data.lot,
            sourceFileName: null,
          });
        } catch {
          /* non-fatal */
        }
      }
      const { blob, filename } = await generatePaaPdf(
        data,
        undefined,
        sessionIdRef.current,
        documentIdRef.current,
        // The form is out, so the server can stop holding two indexed plan sets.
        // See generatePaaPdf — releasing is always safe, and this is the only
        // point at which the review is definitively finished.
        compareIdRef.current,
      );
      // generatePaaPdf just had the server DROP that comparison, so the id is
      // dead. Holding on to it would send the next combine down the "reuse the
      // parked sets" path against a session that no longer exists — one wasted
      // round trip before it uploads both files anyway.
      compareIdRef.current = null;

      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);
      setDownloadName(filename);
      setStep("download");
      toast.success("PAA AI1 generated.");

      // Keep the session id for the combined set: generate ENDS the session and
      // clears the ref, and combining is the step after that. Its telemetry
      // still belongs to this run.
      combineSessionIdRef.current = sessionIdRef.current;

      // Close the session as completed — count the revised sheets.
      if (sessionIdRef.current) {
        const sheetCount = data.rows.filter((r) => r.sheetId.trim().length > 0).length;
        endSession(sessionIdRef.current, {
          totalObjections: sheetCount,
          status: "Completed",
          producedPdf: true,
        }).catch(() => {});
        sessionIdRef.current = null;
      }
    } catch (err) {
      toast.error(`Generation failed: ${(err as Error).message}`);
      setStep("review");
      if (sessionIdRef.current) {
        endSession(sessionIdRef.current, {
          totalObjections: 0,
          status: "Error",
          errorMessage: (err as Error).message,
        }).catch(() => {});
      }
    } finally {
      setBusy(false);
    }
  }, [data]);

  // ---------------------------------------------------------------------------
  // Combine the plan sets — the second artifact of a PAA filing.
  //
  // Reachable from review (where the rows are settled) and from download (where
  // "the PAA is done" actually happens). Both land on the same screen and come
  // back where they left.
  //
  // No AI, no cost: reading the approved set's sheet numbers is the same
  // deterministic title-block scan the review table came from.
  // ---------------------------------------------------------------------------
  const canCombine = useCallback(
    () => !!dataRef.current && !!newFileRef.current && !!oldFileRef.current,
    [],
  );

  const handleCombine = useCallback(
    async (from: Step, destination: Step = "combine", snapshot?: PaaExtraction) => {
      const data = snapshot ?? dataRef.current;
      const updated = newFileRef.current;
      const approved = oldFileRef.current;
      if (!data || !updated || !approved) {
        toast.error("The approved set is needed to combine the plan sets.");
        return;
      }

      combineReturnRef.current = from;

      // Coming back in with the same rows: the plan we already have IS the plan,
      // and re-reading a 60-sheet approved set to compute it again would also
      // throw away whatever the operator had dragged.
      const signature = rowSignature(data);
      if (combinePlan && combineRowsSigRef.current === signature) {
        setStep(destination);
        return;
      }

      setCombineBusy(true);
      // Neutral until the server says which it is: with a live comparison there
      // is nothing to read, and claiming to read a file the tool has just
      // finished describing is how this looked wrong in the first place.
      setCombineProgress("Working out the order…");

      // Two attempts, and the difference between them is ~200MB of upload. A
      // live comparison is already holding both sets, so combining straight
      // after a review sends nothing but the rows; if that session has gone the
      // files follow. Same contract as annotate-stream — an expired id is never
      // an error, it is a slower path.
      const prepare = (withFiles: boolean) =>
        preparePaaCombine(data, {
          approvedFile: withFiles ? approved : null,
          updatedFile: withFiles ? updated : null,
          compareId: withFiles ? null : compareIdRef.current,
          sessionId: sessionIdRef.current ?? combineSessionIdRef.current,
          onProgress: (msg) => setCombineProgress(msg),
        });

      try {
        let prepared;
        try {
          prepared = await prepare(!compareIdRef.current);
        } catch (err) {
          if (!compareIdRef.current) throw err;
          setCombineProgress("Sending both plan sets…");
          prepared = await prepare(true);
        }

        // A previous combine session is superseded — the server cannot work that
        // out for itself, and two parked pairs is two pairs of plan sets held on
        // a box that has room for one.
        if (combineIdRef.current && combineIdRef.current !== prepared.combineId) {
          releasePaaCombine(combineIdRef.current);
        }
        combineIdRef.current = prepared.combineId || null;
        setCombineId(prepared.combineId || null);
        setCombinePlan(prepared.plan);
        setCombineRows(toRows(prepared.plan));
        combineRowsSigRef.current = signature;
        // A new proposal supersedes whatever was built from the last one.
        if (combineFile) URL.revokeObjectURL(combineFile.url);
        setCombineFile(null);
        setStep(destination);
      } catch (err) {
        toast.error(`Could not read the plan sets: ${(err as Error).message}`);
      } finally {
        setCombineBusy(false);
        setCombineProgress(null);
      }
    },
    [combineFile, combinePlan],
  );

  const changeCombineRows = useCallback(
    (next: CombineRow[]) => {
      setCombineRows(next);
      setCombineFile((built) => {
        if (built) URL.revokeObjectURL(built.url);
        return null;
      });
    },
    [],
  );

  const handleBuildCombine = useCallback(
    async (pages: CombinePageRef[], moves: CombineMove[]) => {
      const data = dataRef.current;
      if (!data || pages.length === 0) return;
      setCombineBusy(true);
      try {
        const build = (withFiles: boolean) =>
          buildPaaCombine(pages, data, {
            combineId: withFiles ? null : combineIdRef.current,
            approvedFile: withFiles ? oldFileRef.current : null,
            updatedFile: withFiles ? newFileRef.current : null,
            moves,
            sessionId: sessionIdRef.current ?? combineSessionIdRef.current,
          });

        let result;
        try {
          result = await build(!combineIdRef.current);
        } catch (err) {
          // 410: the server let go of the plan sets while they were reviewing.
          // Losing somebody's reordering over that would be indefensible, so the
          // files go up and the review stands.
          if (err instanceof ApiError && err.status === 410) {
            result = await build(true);
          } else {
            throw err;
          }
        }

        if (combineFile) URL.revokeObjectURL(combineFile.url);
        setCombineFile({
          url: URL.createObjectURL(result.blob),
          name: result.filename,
        });
        toast.success(`Combined plan set ready — ${pages.length} pages.`);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? ((err.body as { error?: string })?.error ?? `HTTP ${err.status}`)
            : (err as Error).message;
        toast.error(`Could not build the combined set: ${message}`);
      } finally {
        setCombineBusy(false);
      }
    },
    [combineFile],
  );

  const handleReset = useCallback(() => {
    // If a session is still open (reviewed but never generated), close it as
    // cancelled so it isn't left "Incomplete" on the dashboard.
    if (sessionIdRef.current) {
      endSession(sessionIdRef.current, {
        totalObjections: 0,
        status: "Cancelled",
      }).catch(() => {});
      sessionIdRef.current = null;
    }
    compareAbortRef.current?.abort();
    compareAbortRef.current = null;
    setCompare({ status: "idle" });
    setCompareCtx(null);
    // Tell the server before dropping our only reference to the id — otherwise
    // the indexed plan sets sit in its memory until the idle timer fires.
    releasePaaCompare(compareIdRef.current);
    compareIdRef.current = null;
    // Same reasoning for the combined-set review: two plan sets parked on the
    // server outlive an abandoned run by 30 minutes otherwise.
    releasePaaCombine(combineIdRef.current);
    combineIdRef.current = null;
    combineSessionIdRef.current = null;
    setCombineId(null);
    setCombinePlan(null);
    setCombineRows([]);
    setCombineProgress(null);
    setCombineBusy(false);
    if (combineFile) URL.revokeObjectURL(combineFile.url);
    setCombineFile(null);
    setStoryDirty(false);
    setRewriting(false);
    setViewingSheet(null);
    setPendingNoteIds(new Set());
    userNoteIdsRef.current = new Set();
    newFileRef.current = null;
    oldFileRef.current = null;
    intentRef.current = "";
    setWantsNotes(NOTES_BY_DEFAULT);
    setOldFileName(null);
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setDownloadUrl(null);
    setDownloadName(null);
    setData(null);
    setProgress([]);
    propertyInfo.reset();
    documentIdRef.current = null;
    lastSyncedDataRef.current = null;
    setStep("upload");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [downloadUrl, combineFile]);

  return (
    <div className="max-w-[1100px]">
      <div className="mb-10">
        <PaaStepIndicator step={step} />
      </div>

      {step === "upload" && (
        <UploadStep
          busy={busy}
          onFile={handleFile}
          fileInputRef={fileInputRef}
          oldFileName={oldFileName}
          onOldFile={(f) => {
            oldFileRef.current = f;
            setOldFileName(f?.name ?? null);
          }}
          onIntent={(v) => {
            intentRef.current = v;
          }}
          wantsNotes={wantsNotes}
          onWantsNotes={setWantsNotes}
        />
      )}

      {step === "processing" && (
        <ProcessingView progress={progress} onCancel={handleCancel} />
      )}

      {step === "review" && data && (
        <ReviewStep
          data={data}
          setData={setData}
          busy={busy}
          googleMapsApiKey={googleMapsApiKey}
          addressVerifying={propertyInfo.addressVerifying}
          savedInfo={propertyInfo.savedInfo}
          savedInfoSearching={propertyInfo.savedInfoSearching}
          onApplySavedInfo={propertyInfo.savedInfo ? propertyInfo.applySavedInfo : undefined}
          compare={compare}
          approvedFileName={oldFileName}
          oldSheets={compareCtx?.oldSheets ?? null}
          approvedSheets={approvedSheets}
          onRepair={repair}
          pendingNoteIds={pendingNoteIds}
          storyDirty={storyDirty}
          rewriting={rewriting}
          rewritableCount={rewritableRows(data).length}
          compareId={compareCtx?.compareId ?? null}
          onViewSheets={setViewingSheet}
          onStartReviewPass={startReviewPass}
          onGistChange={setGist}
          onDesignIntentChange={setDesignIntent}
          onRewrite={() => void handleRewrite()}
          onCompareFile={(f) => void startCompare(f)}
          onCompare={() => {
            const staged = oldFileRef.current;
            if (staged) void startCompare(staged);
          }}
          onCancelCompare={() => compareAbortRef.current?.abort()}
          onRecompareRow={(id) => void recompareRowById(id)}
          onUserEditNote={(id) => userNoteIdsRef.current.add(id)}
          onNoteCommitted={onNoteCommitted}
          onReportRow={onReportRow}
          onGenerate={handleGenerate}
          onCombine={() => void handleCombine("review")}
          canCombine={canCombine()}
          onBack={handleReset}
        />
      )}

      {step === "generating" && (
        <Card>
          <CardBody className="py-16 text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-rule border-t-primary" />
            <div className="mt-4 text-[1rem] text-ink-soft">
              Generating PAA AI1 PDF…
            </div>
          </CardBody>
        </Card>
      )}

      {step === "download" && downloadUrl && downloadName && (
        <DownloadStep
          url={downloadUrl}
          filename={downloadName}
          onReset={handleReset}
          approvedFileName={oldFileName}
          onApprovedFile={(f) => {
            oldFileRef.current = f;
            setOldFileName(f.name);
          }}
          onCombine={() => void handleCombine("download")}
          canCombine={canCombine()}
          combining={combineBusy}
          combined={combineFile}
        />
      )}

      {step === "combine" && combinePlan && (
        <CombineStep
          plan={combinePlan}
          rows={combineRows}
          onRows={changeCombineRows}
          combineId={combineId}
          busy={combineBusy}
          built={combineFile}
          onBuild={(pages, moves) => void handleBuildCombine(pages, moves)}
          onBack={() => setStep(combineReturnRef.current)}
        />
      )}

      {/* Reading a 60-sheet approved set is tens of seconds. Blocking, because
          editing the rows underneath a plan being computed from them would
          produce a review of an order that no longer matches the form. */}
      {combineProgress && step !== "combine" && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/30 px-6 backdrop-blur-sm">
          <div className="w-full max-w-[420px] rounded-xl bg-surface p-8 text-center shadow-lift">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-rule border-t-primary" />
            <div className="mt-5 text-[1.05rem] font-medium text-ink">
              Combining the plan sets
            </div>
            <div className="mt-1.5 text-[0.88rem] text-ink-soft">{combineProgress}</div>
          </div>
        </div>
      )}

      <CompareConfirmDialog
        open={!!compareConfirm}
        sheets={compareConfirm?.sheets ?? 0}
        estimateUsd={compareConfirm?.estimateUsd ?? "0.00"}
        onCancel={() => settleCompareConfirm(false)}
        onConfirm={() => settleCompareConfirm(true)}
      />

      {viewingRow && compareCtx?.compareId && (
        <SheetCompareViewer
          compareId={compareCtx.compareId}
          sheet={viewingRow.sheetId}
          baseline={
            viewingRow.comparedTo?.trim() ||
            viewingRow.compareWith?.trim() ||
            viewingRow.sheetId
          }
          note={viewingRow.changeNote}
          reviewReason={viewingRow.needsReview ? viewingRow.reviewReason : null}
          sequence={reviewSequence}
          pairing={
            approvedSheets.length > 0
              ? {
                  state: pairingFor(viewingRow, approvedSheets),
                  approvedSheets,
                  onChoose: (id) =>
                    repair((rows) => setPairing(rows, viewingRow.id, id)),
                  onSettle: (choice) =>
                    repair((rows) => settleSameNumber(rows, viewingRow.id, choice)),
                  onUnsettle: () =>
                    repair((rows) => unsettleSameNumber(rows, viewingRow.id)),
                  onDropRow: () => {
                    // The page is not part of the filing, so the row goes. Move
                    // on BEFORE it disappears, or the surface is left open on a
                    // sheet that no longer exists.
                    const after = data
                      ? (stepSheet(data.rows, viewingRow.id, 1) ??
                        stepSheet(data.rows, viewingRow.id, -1))
                      : null;
                    repair((rows) => rows.filter((r) => r.id !== viewingRow.id));
                    setViewingSheet(after);
                  },
                }
              : undefined
          }
          onClose={() => setViewingSheet(null)}
        />
      )}

      {compare.status === "running" && (
        <CompareOverlay
          fileName={compare.fileName}
          message={compare.message}
          total={compare.total}
          done={compare.done}
          rowScoped={compare.rowScoped}
          onCancel={() => compareAbortRef.current?.abort()}
        />
      )}
    </div>
  );
}

// =============================================================================
// Step indicator — mirrors the objection flow's 4-step tracker.
// =============================================================================

function PaaStepIndicator({ step }: { step: Step }) {
  const order = ["upload", "processing", "review", "download"] as const;
  // "generating" belongs to review, and "combine" is a side trip off the flow
  // rather than a step of its own — a pip for it would say it is required.
  const effective =
    step === "generating" ? "review" : step === "combine" ? "download" : step;
  const currentIdx = order.indexOf(effective as (typeof order)[number]);
  const steps = [
    { key: "upload", label: "Upload" },
    { key: "processing", label: "Process" },
    { key: "review", label: "Review" },
    { key: "download", label: "Download" },
  ];
  return (
    <ol className="flex w-full items-start gap-2">
      {steps.map((s, i) => {
        const isActive = i === currentIdx;
        const isDone = i < currentIdx;
        return (
          <div key={s.key} className="contents">
            <li className="flex w-[88px] shrink-0 flex-col items-center gap-2">
              <div
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-full text-[0.9rem] font-semibold transition-all",
                  isActive && "bg-primary text-white shadow-soft ring-4 ring-primary/15",
                  isDone && "bg-sage text-white",
                  !isActive && !isDone && "bg-surface-2 text-ink-muted border border-rule",
                )}
              >
                {isDone ? "✓" : i + 1}
              </div>
              <div
                className={cn(
                  "text-[0.82rem] font-medium",
                  isActive && "text-ink",
                  isDone && "text-sage-ink",
                  !isActive && !isDone && "text-ink-muted",
                )}
              >
                {s.label}
              </div>
            </li>
            {i < steps.length - 1 && (
              <li
                aria-hidden
                className={cn(
                  "mt-[18px] h-1 flex-1 rounded-full transition-colors",
                  i < currentIdx ? "bg-sage" : "bg-rule",
                )}
              />
            )}
          </div>
        );
      })}
    </ol>
  );
}

// =============================================================================
// Upload step
// =============================================================================

function UploadStep({
  busy,
  onFile,
  fileInputRef,
  oldFileName,
  onOldFile,
  onIntent,
  wantsNotes,
  onWantsNotes,
}: {
  busy: boolean;
  onFile: (file: File) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  oldFileName: string | null;
  onOldFile: (file: File | null) => void;
  /** Operator-stated reason for the amendment, staged for the run. */
  onIntent: (value: string) => void;
  /** Describe what changed on each sheet? See NOTES_BY_DEFAULT. */
  wantsNotes: boolean;
  onWantsNotes: (next: boolean) => void;
}) {
  const whatChanged = useWhatChangedEnabled();
  const [intent, setIntent] = useState("");
  // Stage the NEW file instead of starting immediately — otherwise processing
  // would begin the instant it's dropped, leaving no chance to add the approved
  // set. Both files are chosen first, then one explicit "Build" starts the run.
  const [newFile, setNewFile] = useState<File | null>(null);

  // Both plan PDFs are chosen the same way — dropped on their card or browsed
  // for. The approved set used to be browse-only, which is the one asymmetry an
  // operator who just dragged the updated plan in runs straight into.
  const updated = usePlanPdfDrop(setNewFile, busy);
  const approved = usePlanPdfDrop(onOldFile, busy);

  return (
    <div className="animate-reveal max-w-3xl">
      <div className="mb-8">
        <div className="label mb-2">Post-Approval Amendment</div>
        <h1 className="text-[clamp(2.25rem,4.5vw,3.25rem)] font-medium tracking-tight text-ink">
          Build a PAA AI1
        </h1>
        <p className="mt-3 max-w-xl text-[1rem] leading-relaxed text-ink-soft">
          {whatChanged ? (
            <>
              Choose the updated plan PDF, and add the approved set if you have
              it — that is what change notes and the combined plan set are both
              built from. We&apos;ll read each sheet&apos;s revision from its
              title block, fill Section 2 and the Section 3 change summary, and
              check the PAA box for you. Review before generating.
            </>
          ) : (
            <>
              Choose the updated plan PDF, then build. We&apos;ll read each
              sheet&apos;s revision from its title block, fill Section 2, and check
              the PAA box for you. Review before generating.
            </>
          )}
        </p>
      </div>
      <Card>
        <CardBody>
          <label
            {...updated.dropProps}
            className={cn(
              "relative block cursor-pointer rounded-2xl border-2 border-dashed px-8 py-12 text-center transition-all",
              updated.dragging
                ? "border-primary bg-primary-soft shadow-soft"
                : "border-rule-strong bg-surface/60 hover:border-primary hover:bg-primary-soft/40",
              busy && "pointer-events-none opacity-60",
            )}
          >
            <input ref={fileInputRef} {...updated.inputProps} />
            <div className="label mb-1 text-primary">Updated plan (required)</div>
            <div className="text-[1.2rem] font-medium text-ink">
              {newFile ? (
                <>Selected: <span className="text-primary">{newFile.name}</span></>
              ) : (
                <>
                  Drop the updated plan PDF or{" "}
                  <span className="text-primary underline decoration-primary/30 decoration-2 underline-offset-4">
                    click to browse
                  </span>
                </>
              )}
            </div>
            <div className="mt-2 text-[0.9rem] text-ink-muted">
              {newFile ? "Click to choose a different file" : "PDF only · 100MB max"}
            </div>
          </label>

          {/* Optional: the prior APPROVED set, to auto-describe what changed
              on each superseding sheet in the Section 3 list. Hidden while the
              "what changed" feature is disabled (see flags.ts). */}
          {whatChanged && (
          <div
            {...approved.dropProps}
            className={cn(
              "mt-5 rounded-xl border bg-surface/40 px-5 py-4 transition-colors",
              approved.dragging
                ? "border-dashed border-primary bg-primary-soft/60"
                : "border-rule",
            )}
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-[0.95rem] font-medium text-ink">
                  Add the approved set{" "}
                  <span className="text-ink-muted">(optional)</span>
                </div>
                <div className="mt-1 text-[0.85rem] text-ink-muted">
                  {approved.dragging
                    ? "Drop the approved PDF here"
                    : oldFileName
                      ? `Using: ${oldFileName}`
                      : "Drop the approved PDF here, or choose it. It has two uses: describing what changed on each sheet, and building the combined plan set. Can also be added later, on the review page."}
                </div>
              </div>
              <label
                className={cn(
                  "shrink-0 cursor-pointer rounded-lg border border-rule-strong px-3 py-2 text-[0.85rem] font-medium text-ink hover:border-primary hover:bg-primary-soft/40",
                  busy && "pointer-events-none opacity-60",
                )}
              >
                <input {...approved.inputProps} />
                {oldFileName ? "Change" : "Choose approved PDF"}
              </label>
            </div>

            {/* ONE question, next to the file it is about. The approved set
                has two uses and only this one costs anything, so this is the
                only thing worth asking — and it is checked, because notes are
                what the office normally wants and the alternative is a click on
                every filing. Unticking it is how somebody who came for the
                combined plan set avoids paying for a pass they never wanted. */}
            {oldFileName && (
              <label className="mt-4 flex cursor-pointer items-start gap-3 border-t border-rule pt-4">
                <input
                  type="checkbox"
                  checked={wantsNotes}
                  disabled={busy}
                  onChange={(e) => onWantsNotes(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
                />
                <span className="min-w-0">
                  <span className="block text-[0.9rem] font-medium text-ink">
                    Describe what changed on each sheet
                  </span>
                  <span className="mt-0.5 block text-[0.84rem] leading-relaxed text-ink-muted">
                    Reads both drawings of every revised sheet — a few cents a
                    sheet, and minutes rather than seconds. Untick it if you only
                    need the combined plan set; you can still ask for notes on
                    the review page.
                  </span>
                </span>
              </label>
            )}

            {/* Why the amendment exists, in the operator's words — the only way
                to state something no sheet says in words ("adding a 7th
                floor"). Asked here so it is in hand before the comparison
                starts; also editable on the review page. Shown only when notes
                were asked for: it seeds the driver derivation and nothing else,
                so with the box unticked it is a question about work that is not
                going to happen. */}
            {oldFileName && wantsNotes && (
              <div className="mt-4 border-t border-rule pt-4">
                <label
                  htmlFor="paa-upload-intent"
                  className="text-[0.9rem] font-medium text-ink"
                >
                  Why is this amendment being filed?{" "}
                  <span className="text-ink-muted">(optional)</span>
                </label>
                <Textarea
                  id="paa-upload-intent"
                  rows={2}
                  value={intent}
                  disabled={busy}
                  onChange={(e) => {
                    setIntent(e.target.value);
                    onIntent(e.target.value);
                  }}
                  placeholder="e.g. “minor as-built changes prior to DOB inspection” or “adding a 7th floor”"
                  className="mt-2 resize-none text-[0.88rem]"
                />
                <div className="mt-1.5 text-[0.85rem] text-ink-muted">
                  Prints as the opening line above the sheet list.
                </div>
              </div>
            )}
          </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            {whatChanged && newFile && !oldFileName && (
              <span className="text-[0.85rem] text-ink-muted">
                No approved set — add it now or on the review page
              </span>
            )}
            <Button onClick={() => newFile && onFile(newFile)} disabled={!newFile || busy}>
              Build PAA AI1
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

// =============================================================================
// Processing step — live activity log over SSE.
// =============================================================================

function ProcessingView({
  progress,
  onCancel,
}: {
  progress: string[];
  onCancel: () => void;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [progress]);
  return (
    <div className="animate-reveal max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-rule border-t-primary" />
        <h1 className="text-[1.5rem] font-medium text-ink">Reading the plan set…</h1>
      </div>
      <Card>
        <CardBody>
          <div className="max-h-[360px] overflow-y-auto rounded-md bg-canvas-2 p-4 font-mono text-[0.82rem] leading-relaxed text-ink-soft">
            {progress.length === 0 ? (
              <div className="text-ink-muted">Starting…</div>
            ) : (
              progress.map((m, i) => (
                <div key={i} className="whitespace-pre-wrap">
                  {m}
                </div>
              ))
            )}
            <div ref={endRef} />
          </div>
          <div className="mt-4 flex justify-end">
            <Button intent="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

// =============================================================================
// Review step
// =============================================================================

function ReviewStep({
  data,
  setData,
  busy,
  googleMapsApiKey,
  addressVerifying,
  savedInfo,
  savedInfoSearching,
  onApplySavedInfo,
  compare,
  approvedFileName,
  oldSheets,
  approvedSheets,
  onRepair,
  onStartReviewPass,
  pendingNoteIds,
  storyDirty,
  rewriting,
  rewritableCount,
  compareId,
  onViewSheets,
  onNoteCommitted,
  onReportRow,
  onGistChange,
  onDesignIntentChange,
  onRewrite,
  onCompareFile,
  onCompare,
  onCancelCompare,
  onRecompareRow,
  onUserEditNote,
  onGenerate,
  onCombine,
  canCombine,
  onBack,
}: {
  data: PaaExtraction;
  setData: React.Dispatch<React.SetStateAction<PaaExtraction | null>>;
  busy: boolean;
  googleMapsApiKey: string | null;
  addressVerifying: boolean;
  savedInfo: PropertyInfoDto | null;
  savedInfoSearching: boolean;
  onApplySavedInfo?: () => void;
  compare: CompareState;
  /** The approved set in hand — having it is not the same as having asked for a
   *  comparison, which is why the panel offers one instead of assuming. */
  approvedFileName: string | null;
  oldSheets: PaaOldSheet[] | null;
  /** The approved set's pages, for the pairing control on each row. Empty until
   *  something has read that set — a comparison or a combine plan. */
  approvedSheets: readonly ApprovedSheet[];
  /** Apply one of the pairing helpers to the rows. Not a plain patch: changing
   *  what a page supersedes also drops the row's confirmation, and that rule
   *  lives in one place. */
  onRepair: (next: (rows: PaaRow[]) => PaaRow[]) => void;
  pendingNoteIds: ReadonlySet<string>;
  storyDirty: boolean;
  rewriting: boolean;
  rewritableCount: number;
  /** Live comparison id — the side-by-side viewer renders from it. Null once
   *  it has expired or before any comparison has run. */
  compareId: string | null;
  onViewSheets: (row: PaaRow) => void;
  /** Open the one-sheet-at-a-time pass. Absent until a comparison exists —
   *  there are no drawings to step through before then. */
  onStartReviewPass: () => void;
  /** One sheet's note was committed — capture it for the revision log. */
  onNoteCommitted: (rowId: string) => void;
  /** Report a misdiagnosed or missed change on one sheet. */
  onReportRow: (rowId: string, kind: ReportKind, message: string) => Promise<void>;
  onGistChange: (value: string) => void;
  onDesignIntentChange: (value: string) => void;
  onRewrite: () => void;
  onCompareFile: (file: File) => void;
  /** Run the comparison on the approved set already in hand. */
  onCompare: () => void;
  onCancelCompare: () => void;
  onRecompareRow: (id: string) => void;
  onUserEditNote: (id: string) => void;
  onGenerate: () => void;
  /** Build the combined plan set. Gated exactly like Generate, plus needing the
   *  approved set — a combine with nothing to combine against is not a thing to
   *  offer and then explain. */
  onCombine: () => void;
  canCombine: boolean;
  onBack: () => void;
}) {
  const whatChanged = useWhatChangedEnabled();
  // Set-wide outcome of the last comparison, counted from the ROWS rather than
  // reported by the engine — so the headline can never disagree with the table
  // right below it, including after the operator edits a row.
  const tally = useMemo<CompareTally | null>(() => {
    const compared = data.rows.filter((r) => r.verdict != null);
    if (compared.length === 0) return null;
    return {
      unchanged: compared.filter((r) => r.verdict === "Unchanged" && !r.needsReview).length,
      described: compared.filter((r) => !r.needsReview && isChangedVerdict(r.verdict)).length,
      needsReview: compared.filter((r) => r.needsReview).length,
      staleRevision: compared.filter((r) => r.revisionLooksStale).length,
    };
  }, [data.rows]);

  // Counted from the rows on screen, never reported separately: two sources for
  // one number eventually disagree and the one she is looking at is the true one.
  const progress = useMemo(() => reviewProgress(data.rows), [data.rows]);

  // The two pairing mistakes, counted separately because they are opposites
  // with opposite fixes: a page superseding nothing, and a page filed as new
  // whose number is already approved. Neither blocks anything.
  // Only once something has READ the approved set. Computed against an empty
  // list every "S" row looks like a sheet that supersedes nothing, so the
  // header announced "11 with nothing matched" while the comparison was still
  // uploading — a loud, specific, wrong claim about a file nobody had opened.
  const pairings = useMemo(
    () =>
      approvedSheets.length > 0
        ? pairingProgress(data.rows, approvedSheets)
        : { total: 0, unmatched: 0, newButPresent: 0 },
    [data.rows, approvedSheets],
  );

  const patch = (p: Partial<PaaExtraction>) => setData((d) => (d ? { ...d, ...p } : d));

  const updateRow = (id: string, p: Partial<PaaRow>) => {
    // A note the user typed must survive any (re-)comparison — remember it.
    if ("changeNote" in p) onUserEditNote(id);
    setData((d) =>
      d ? { ...d, rows: d.rows.map((r) => (r.id === id ? applyRowPatch(r, p) : r)) } : d,
    );
  };
  const addRow = () => setData((d) => (d ? { ...d, rows: [...d.rows, makeEmptyRow()] } : d));
  const removeRow = (id: string) =>
    setData((d) => (d ? { ...d, rows: d.rows.filter((r) => r.id !== id) } : d));

  const validRows = data.rows.filter((r) => r.sheetId.trim().length > 0);
  const undetectedCount = data.rows.filter(
    (r) => r.sourcePage != null && r.sheetId.trim().length === 0,
  ).length;

  const issues: string[] = [];
  if (!data.address?.trim()) issues.push("Property Address is required");
  if (!data.jobNumber?.trim()) issues.push("Job / BIS doc number is required");
  if (validRows.length === 0) issues.push("Add at least one sheet revision");
  const canGenerate = issues.length === 0;
  const comparing = compare.status === "running";

  return (
    <div className="animate-reveal max-w-5xl">
      <div className="mb-8">
        <div className="label mb-2">Review</div>
        <h1 className="text-[clamp(2.25rem,4.5vw,3.25rem)] font-medium tracking-tight text-ink">
          Review the amendment
        </h1>
        <p className="mt-3 max-w-xl text-[1rem] leading-relaxed text-ink-soft">
          Verify the property details and the revised sheets before generating
          the AI1.
        </p>
      </div>

      {/* Property */}
      <PropertyInfoForm
        data={data}
        onPatch={patch}
        savedInfo={savedInfo}
        savedInfoSearching={savedInfoSearching}
        onApplySavedInfo={onApplySavedInfo}
        googleMapsApiKey={googleMapsApiKey}
        addressVerifying={addressVerifying}
      />

      {/* Filing type. Sits with the property because that is what it is — a
          fact about the filing, not about a sheet — and because the download
          name is built from these same fields. */}
      <FilingTypeField
        value={data.filingType ?? ""}
        userDefined={data.filingTypeUserDefined ?? false}
        onChange={(filingType, filingTypeUserDefined) =>
          patch({ filingType, filingTypeUserDefined })
        }
      />

      {/* The amendment's story, ahead of the sheet-by-sheet detail: one
          paragraph is reviewable, thirty notes are not. */}
      {whatChanged && (
        <WhatChangedPanel
          compare={compare}
          tally={tally}
          gist={data.gist ?? ""}
          designIntent={data.designIntent ?? ""}
          dirty={storyDirty}
          rewriting={rewriting}
          rewritableCount={rewritableCount}
          busy={busy}
          approvedFileName={approvedFileName}
          // The estimate the operator decides on, computed from the rows they
          // are looking at — the same count the run itself will bill for.
          compareSheets={aiSheetCount(data.rows, new Set())}
          compareEstimateUsd={(
            aiSheetCount(data.rows, new Set()) * EST_USD_PER_SHEET
          ).toFixed(2)}
          onCompare={onCompare}
          onGistChange={onGistChange}
          onDesignIntentChange={onDesignIntentChange}
          onRewrite={onRewrite}
          onFile={onCompareFile}
          onCancel={onCancelCompare}
        />
      )}

      {/* Section 2 — sheet revisions */}
      <section className="mt-6 rounded-lg border border-rule bg-surface shadow-soft">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-6 py-3.5">
          <div className="flex items-center gap-3">
            <div className="text-[0.95rem] font-semibold text-ink">
              Section 2 — Revised sheets
            </div>
            <span className="inline-flex items-center rounded-full bg-primary-soft px-2.5 py-0.5 text-[0.74rem] font-medium text-primary-ink">
              {validRows.length} {validRows.length === 1 ? "sheet" : "sheets"}
            </span>
            {/* Work to do, not a tally of mistakes — and two numbers rather than
                one, because a page that supersedes nothing and a page filed as
                new over an approved sheet are opposite problems with opposite
                fixes. Both are settled on the row itself. */}
            {pairings.unmatched > 0 && (
              <span className="inline-flex items-center rounded-full border border-warn/50 bg-warn-soft px-2.5 py-0.5 text-[0.74rem] font-medium text-ink-soft">
                {pairings.unmatched} with nothing matched
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* An accelerator, not a gate: the table below keeps working exactly
                as it did, and a user who only wants a combined set never comes
                here. It is offered because stepping is faster for the operator
                who has to look at all of them — which is the whole job. */}
            {compareId && progress.total > 0 && (
              <Button intent="soft" size="sm" onClick={onStartReviewPass}>
                {progress.reviewed === 0
                  ? "Review one at a time"
                  : progress.remaining === 0
                    ? "All confirmed — look again"
                    : `Continue — ${progress.remaining} to confirm`}
              </Button>
            )}
            <span className="text-[0.82rem] tabular-nums text-ink-muted">
              {progress.reviewed > 0 && progress.total > 0
                ? `${progress.reviewed} of ${progress.total} confirmed`
                : "16 fit per AI1 page"}
            </span>
          </div>
        </header>
        <div className="border-b border-rule bg-butter-soft px-6 py-2.5">
          <div className="flex items-center gap-2 text-[0.85rem] text-butter-ink">
            <span aria-hidden>✨</span>
            One row per page, read from each page&apos;s title block — please review.
          </div>
        </div>
        <SameNumberBanner
          count={pairings.newButPresent}
          disabled={comparing}
          onSettleAll={() =>
            onRepair((rows) => settleAllSameNumber(rows, approvedSheets))
          }
        />
        {undetectedCount > 0 && (
          <div className="border-b border-rule bg-warn-soft px-6 py-2.5">
            <div className="flex items-center gap-2 text-[0.85rem] text-ink">
              <span aria-hidden>⚠️</span>
              {undetectedCount} page{undetectedCount === 1 ? "" : "s"} had no
              detectable revision (highlighted below) — enter the Sheet ID +
              Revision, or remove the row.
            </div>
          </div>
        )}
        <div className="px-6 py-5">
          <div className="overflow-x-auto">
            <table className="w-full text-[0.9rem]">
              <thead>
                <tr className="border-b border-rule text-left text-[0.78rem] uppercase tracking-wide text-ink-muted">
                  <th className="px-2 py-2 font-medium w-16">Page</th>
                  <th className="px-2 py-2 font-medium">Sheet ID</th>
                  <th className="px-2 py-2 font-medium w-28">Revision</th>
                  <th className="px-2 py-2 font-medium w-40">Action</th>
                  <th className="px-2 py-2 font-medium">AI1 page ID</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <RowEditor
                    key={row.id}
                    row={row}
                    notePending={pendingNoteIds.has(row.id)}
                    oldSheets={oldSheets}
                    approvedSheets={approvedSheets}
                    compareBusy={comparing}
                    onRecompare={() => onRecompareRow(row.id)}
                    onViewSheets={
                      compareId ? () => onViewSheets(row) : undefined
                    }
                    onChoosePairing={(id) =>
                      onRepair((rows) => setPairing(rows, row.id, id))
                    }
                    onSettleSameNumber={(choice) =>
                      onRepair((rows) => settleSameNumber(rows, row.id, choice))
                    }
                    onUnsettleSameNumber={() =>
                      onRepair((rows) => unsettleSameNumber(rows, row.id))
                    }
                    onChange={(p) => updateRow(row.id, p)}
                    onNoteCommitted={() => onNoteCommitted(row.id)}
                    onReport={(kind, message) => onReportRow(row.id, kind, message)}
                    onRemove={() => removeRow(row.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <Button intent="ghost" size="sm" onClick={addRow}>
              + Add row
            </Button>
            <span className="text-[0.82rem] text-ink-muted">
              Rev 0.00 = new sheet (N) · Rev ≥ 0.01 = supersedes (S)
            </span>
          </div>
        </div>
      </section>

      {issues.length > 0 && (
        <div className="mt-6 rounded-lg border border-warn/30 bg-warn-soft px-4 py-3">
          <div className="text-[0.88rem] font-semibold text-ink">
            {issues.length} {issues.length === 1 ? "issue" : "issues"} to fix before generating
          </div>
          <ul className="mt-1.5 space-y-1 text-[0.88rem] text-ink-soft">
            {issues.map((msg, i) => (
              <li key={i} className="flex items-start gap-2">
                <span aria-hidden className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-warn" />
                <span>{msg}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <Button intent="ghost" size="md" onClick={onBack} disabled={busy}>
          ← Start over
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            intent="secondary"
            size="md"
            onClick={onCombine}
            disabled={busy || !canGenerate || comparing || !canCombine}
            title={
              !canCombine
                ? "Add the approved set above to combine the two plan sets"
                : comparing
                  ? "Comparison in progress — wait for it to finish or cancel it"
                  : !canGenerate
                    ? "Fix the issues above first"
                    : "The approved set with these sheets swapped in"
            }
          >
            Combine plan sets
          </Button>
        <Button
          intent="primary"
          size="md"
          onClick={onGenerate}
          disabled={busy || !canGenerate || comparing}
          title={
            comparing
              ? "Comparison in progress — wait for it to finish or cancel it"
              : !canGenerate
                ? "Fix the issues above first"
                : undefined
          }
        >
          Generate AI1 PDF →
        </Button>
        </div>
      </div>
    </div>
  );
}

// What the combined-set plan depends on: which sheet, what action, which page of
// the updated PDF, and the operator's baseline. Everything else on a row (notes,
// verdicts, hints) cannot move a page, so an edit to it must not cost a re-read
// of the approved set — or the operator's dragging.
function rowSignature(data: PaaExtraction): string {
  return data.rows
    .map((r) =>
      [r.sheetId.trim().toUpperCase(), r.action, r.revision, r.sourcePage ?? "", r.compareWith ?? ""].join(
        "|",
      ),
    )
    .join(";");
}

// The page-ID columns are derived (not user-edited), so recompute them from
// sheet + revision + action on every relevant change. Editing the revision also
// auto-sets the action (.00 = new, .01+ = supersede) unless the user is
// changing the action in the same patch.
function applyRowPatch(row: PaaRow, patch: Partial<PaaRow>): PaaRow {
  const next: PaaRow = { ...row, ...patch };
  const revChanged = "revision" in patch;
  const sheetChanged = "sheetId" in patch;
  const actionChanged = "action" in patch;

  if (revChanged && !actionChanged && !next.actionLocked) {
    next.action = parseRevisionMinor(next.revision) <= 0 ? "N" : "S";
  }
  if (revChanged || sheetChanged || actionChanged) {
    const ids = pageIdsFor(next.sheetId, next.revision, next.action);
    next.originalNewOmitPageId = ids.originalNewOmitPageId;
    next.supersedingPageId = ids.supersedingPageId;
  }
  return next;
}

// Compact, read-only summary of what this row will print in the AI1 grid.
function pageIdSummary(row: PaaRow): string {
  if (!row.sheetId.trim()) return "";
  if (row.action === "S") {
    return `${row.originalNewOmitPageId} → ${row.supersedingPageId}`;
  }
  return row.originalNewOmitPageId; // N / O: single page id
}

// Sizes the change-note textarea to its content: 1 row when the note fits,
// growing with wrap. Used both as the mount ref and from onChange.
function autoGrowNote(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

function RowEditor({
  row,
  notePending,
  oldSheets,
  approvedSheets,
  compareBusy,
  onRecompare,
  onViewSheets,
  onChoosePairing,
  onSettleSameNumber,
  onUnsettleSameNumber,
  onChange,
  onNoteCommitted,
  onReport,
  onRemove,
}: {
  row: PaaRow;
  /** True while a running comparison is still computing this row's note. */
  notePending?: boolean;
  /** Sheets found in the approved set (baseline dropdown options); null until
   *  a comparison has run, which hides the per-row compare controls. */
  oldSheets: PaaOldSheet[] | null;
  /** The approved set's pages, from whichever read is in hand. Empty means
   *  nothing has read it yet, and the pairing has nothing to offer. */
  approvedSheets: readonly ApprovedSheet[];
  /** True while a full comparison is running — per-row controls disabled. */
  compareBusy: boolean;
  onRecompare: () => void;
  /** Open the side-by-side drawing viewer. Absent when no live comparison is
   *  held (nothing to render from). */
  onViewSheets?: () => void;
  /** What this page supersedes — the same decision the drawing surface asks. */
  onChoosePairing: (approvedSheetId: string | null) => void;
  onSettleSameNumber: (choice: "updated" | "both") => void;
  onUnsettleSameNumber: () => void;
  onChange: (patch: Partial<PaaRow>) => void;
  /** The note field lost focus — the edit is a considered one, so record it. */
  onNoteCommitted: () => void;
  /** File a report against this sheet: we read it wrong, or we missed a change. */
  onReport?: (kind: ReportKind, message: string) => Promise<void>;
  onRemove: () => void;
}) {
  // Per-row pairing picker (dropdown + AI hint), tucked behind the chip.
  const [compareOpen, setCompareOpen] = useState(false);
  const undetected = row.sourcePage != null && !row.sheetId.trim();
  // Shown when an approved set was compared (server set a note) or while a
  // running comparison is about to fill it in (pending placeholder).
  const hasNote = row.changeNote != null;
  // Per-row compare controls exist once a comparison context is known, and
  // only for "S" (superseding) rows — "N" sheets are new (nothing to compare)
  // and "O" sheets are removed (no new page to diff).
  const canCompare =
    oldSheets != null && row.sheetId.trim().length > 0 && row.action === "S";

  // The pairing is asked of "N" rows too, and that is the point: a page marked
  // new is exactly where a renumbered sheet hides, and where the same-number
  // collision lives. An "O" row reads backwards — it names the approved sheet
  // leaving the set — so there is nothing to choose on it.
  const pairing =
    approvedSheets.length > 0 && row.sheetId.trim().length > 0 && row.action !== "O"
      ? pairingFor(row, approvedSheets)
      : null;
  const showNoteRow = hasNote || notePending || canCompare || !!pairing;
  const controlsDisabled = compareBusy || !!notePending;

  // What the chip says: where this page is pointed, and — when the last
  // comparison used something else — that the note is about a different pair.
  const chipText = pairing
    ? pairing.approvedSheetId
      ? `vs ${pairing.approvedSheetId}${pairing.approvedPage != null ? ` · p.${pairing.approvedPage}` : ""}`
      : row.action === "N"
        ? "new — supersedes nothing"
        : "nothing matched"
    : row.comparedTo
      ? `vs ${row.comparedTo}`
      : row.oldSheetMissing
        ? "no match in approved set"
        : "choose baseline";
  return (
    <>
    {/* When the sheet has a note the pair reads as ONE unit: no divider here —
        the single bottom border lives on the note row. */}
    <tr
      className={cn(
        "transition-colors",
        !showNoteRow && "border-b border-rule/60 last:border-b-0",
        undetected && "bg-warn-soft/50",
        row.reviewed && !undetected && "bg-sage-soft/25",
      )}
    >
      <td className="px-2 py-2 w-16 text-center text-[0.82rem] text-ink-muted tabular-nums">
        {row.sourcePage ?? "—"}
      </td>
      <td className="px-2 py-2">
        <Input
          value={row.sheetId}
          placeholder={undetected ? "not detected — enter sheet" : "A-101"}
          onChange={(e) => onChange({ sheetId: e.target.value.toUpperCase() })}
          className={undetected ? "border-warn/60" : undefined}
        />
      </td>
      <td className="px-2 py-2 w-28">
        <Input
          value={row.revision}
          placeholder="0.01"
          onBlur={(e) => {
            const minor = parseRevisionMinor(e.target.value);
            const normalized = formatRevision(minor);
            if (normalized !== row.revision) onChange({ revision: normalized });
          }}
          onChange={(e) => onChange({ revision: e.target.value })}
        />
      </td>
      <td className="px-2 py-2 w-40">
        <select
          value={row.action}
          onChange={(e) =>
            onChange({ action: e.target.value as PaaAction, actionLocked: true })
          }
          className="w-full rounded-md border border-rule bg-surface px-2.5 py-2 text-[0.9rem] text-ink focus:border-primary focus:outline-none focus:shadow-ring"
        >
          <option value="N">N · New</option>
          <option value="S">S · Supersede</option>
          <option value="O">O · Omit</option>
        </select>
      </td>
      <td className="px-2 py-2 font-mono text-[0.84rem] text-ink-soft whitespace-nowrap">
        {pageIdSummary(row)}
      </td>
      <td className="px-2 py-2 w-12 text-right">
        <button
          type="button"
          onClick={onRemove}
          className="rounded-md p-1.5 text-ink-muted transition-colors hover:bg-surface-2 hover:text-danger"
          aria-label="Remove row"
        >
          ×
        </button>
      </td>
    </tr>
    {showNoteRow && (
      <tr
        className={cn(
          "border-b border-rule/60 transition-colors last:border-b-0",
        )}
      >
        {/* Full width, no label column: the note is the widest thing on the
            row and a "What changed" caption bought a permanent empty gutter to
            say what the placeholder already says. */}
        <td colSpan={6} className="px-2 pb-3 pt-0">
          <div className="flex items-start gap-2">
            {!hasNote && notePending ? (
              <span className="mt-1.5 inline-flex flex-1 items-center gap-2 text-[0.82rem] text-ink-muted">
                <span
                  aria-hidden
                  className="h-3 w-3 animate-spin rounded-full border-2 border-rule border-t-primary"
                />
                comparing…
              </span>
            ) : (
            <>
            {row.oldSheetMissing && !canCompare && (
              <span
                className="mt-1.5 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-warn/40 bg-warn-soft px-2.5 py-0.5 text-[0.74rem] font-medium text-ink-soft"
                title="An approved set was uploaded, but no matching sheet was found in it to compare against. Write the change note manually."
              >
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-warn" />
                not in approved set
              </span>
            )}
            {row.reviewed && (
              <button
                type="button"
                onClick={() => onChange({ reviewed: false })}
                title="You confirmed this sheet — click to un-confirm"
                className="mt-1.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-sage/50 bg-sage-soft px-2 py-0.5 text-[0.72rem] font-medium text-sage-ink transition-colors hover:border-sage"
              >
                ✓ confirmed
              </button>
            )}
            <VerdictChip row={row} />
            <Textarea
              ref={autoGrowNote}
              rows={1}
              value={row.changeNote ?? ""}
              placeholder="What changed on this sheet"
              onChange={(e) => {
                onChange({ changeNote: e.target.value });
                autoGrowNote(e.target);
              }}
              // Recorded when the field is COMMITTED, not per keystroke. Filing
              // is not the only moment worth capturing — an operator who
              // rewrites a dozen notes and never generates would otherwise
              // leave no trace, and those rewrites are the data.
              onBlur={onNoteCommitted}
              className="min-h-0 flex-1 resize-none overflow-hidden py-1.5 text-[0.85rem]"
            />
            </>
            )}
            {(canCompare || pairing) && (
              <div className="flex shrink-0 items-center gap-1 pt-1">
                {/* The note says what changed; this shows it. Labelled, not a
                    bare glyph — verifying the note against the drawings is the
                    most valuable thing on this row and nobody hunts for an icon
                    they've never seen. Only on superseding rows: an "N" sheet
                    has no approved page to sit beside, an "O" sheet no new one. */}
                {onViewSheets && canCompare && (
                  <button
                    type="button"
                    onClick={onViewSheets}
                    title={`See approved and updated ${row.sheetId} side by side`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-rule-strong bg-surface px-2.5 py-0.5 text-[0.74rem] font-medium text-ink-soft transition-colors hover:border-primary hover:bg-primary-soft/40 hover:text-ink"
                  >
                    <CompareIcon className="h-3.5 w-3.5" />
                    Compare
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setCompareOpen((o) => !o)}
                  disabled={controlsDisabled}
                  title="What this page supersedes in the approved set"
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[0.74rem] font-medium transition-colors disabled:opacity-50",
                    pairing?.unmatched || (!pairing && row.oldSheetMissing)
                      ? "border-warn/40 bg-warn-soft text-ink-soft hover:border-warn"
                      : pairing?.approvedSheetId || row.comparedTo
                        ? "border-rule-strong bg-surface text-ink-soft hover:border-primary hover:text-ink"
                        : "border-dashed border-rule-strong text-ink-muted hover:border-primary hover:text-ink",
                  )}
                >
                  {(pairing?.unmatched || (!pairing && row.oldSheetMissing && !row.comparedTo)) && (
                    <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-warn" />
                  )}
                  {chipText}
                  <ChevronIcon
                    className={cn(
                      "h-3 w-3 transition-transform",
                      compareOpen && "rotate-180",
                    )}
                  />
                </button>
                <button
                  type="button"
                  onClick={onRecompare}
                  disabled={controlsDisabled}
                  title={`Re-compare ${row.sheetId} now`}
                  aria-label={`Re-compare ${row.sheetId}`}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink disabled:opacity-50"
                >
                  {notePending ? (
                    <span
                      aria-hidden
                      className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-rule border-t-primary"
                    />
                  ) : (
                    <RefreshIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            )}
          </div>
          {/* The evidence the note was written from. Collapsed by default —
              the note stays the row's subject and this is how you check it. */}
          {!notePending && (
            <SheetFindings row={row} onCompare={onViewSheets} onReport={onReport} />
          )}

          {/* Why the tool reached its verdict, and the two things it cannot
              settle on its own. Below the note, so the note stays the row's
              subject and these read as annotations on it. */}
          {!notePending && (
            <ReviewNotice
              row={row}
              onFixRevision={() =>
                onChange({
                  action: "S",
                  actionLocked: true,
                  revision: formatRevision(parseRevisionMinor(row.revision) + 1),
                })
              }
            />
          )}
          {/* The same-number collision is NOT behind the chip. It is a question
              with no default, and the outcome of not answering it is a filing
              carrying two versions of one drawing — so it is on the row, in
              front of her, exactly as it is on the drawing surface. */}
          {pairing && (
            <SameNumberPrompt
              pairing={pairing}
              disabled={controlsDisabled}
              onSettle={onSettleSameNumber}
              onUnsettle={onUnsettleSameNumber}
              onDropRow={onRemove}
            />
          )}
          {(canCompare || pairing) && compareOpen && (
            <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-rule bg-canvas-2 px-3 py-2.5">
              {pairing ? (
                <PairingChoice
                  pairing={pairing}
                  approvedSheets={approvedSheets}
                  disabled={controlsDisabled}
                  onChoose={onChoosePairing}
                />
              ) : (
                <label className="flex min-w-[15rem] flex-1 items-center gap-2">
                  <span className="label shrink-0 text-ink-muted">Supersedes</span>
                  <select
                    value={row.compareWith ?? ""}
                    onChange={(e) => onChange({ compareWith: e.target.value || null })}
                    disabled={controlsDisabled}
                    className="min-w-0 flex-1 rounded-md border border-rule bg-surface px-2.5 py-1.5 text-[0.85rem] text-ink focus:border-primary focus:outline-none focus:shadow-ring disabled:opacity-50"
                  >
                    <option value="">Auto — {row.sheetId.trim() || "same sheet"}</option>
                    {(oldSheets ?? []).map((s) => (
                      <option key={s.sheet} value={s.sheet}>
                        {s.sheet} · p.{s.page} of approved set
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {canCompare && (
                <>
                  <Input
                    value={row.compareHint ?? ""}
                    onChange={(e) => onChange({ compareHint: e.target.value })}
                    disabled={controlsDisabled}
                    placeholder="Optional note to the AI — e.g. broken out from T-203, carried-over content isn't a change"
                    className="min-w-[240px] flex-1 text-[0.85rem]"
                  />
                  <Button
                    intent="soft"
                    size="sm"
                    onClick={onRecompare}
                    disabled={controlsDisabled}
                    className="inline-flex items-center gap-1.5"
                  >
                    <RefreshIcon className="h-3.5 w-3.5" />
                    Re-compare
                  </Button>
                </>
              )}
            </div>
          )}
        </td>
      </tr>
    )}
    </>
  );
}

// "We checked this sheet and the drawing matched."
//
// This chip is load-bearing, not decoration. An unchanged sheet's note is left
// EMPTY on purpose — nothing changed, and the client does not want "No change."
// printed on a filed AI1 — which means a blank note now covers two very
// different situations: one we examined and cleared, and one nobody has written
// yet. The chip is the only thing that tells them apart, so it carries the
// verdict's own reasoning in its tooltip.
//
// A changed sheet needs no chip: the note IS the change. A sheet that could not
// be compared gets no chip either — it is always flagged, and ReviewNotice says
// so far more loudly than a pill could.
function VerdictChip({ row }: { row: PaaRow }) {
  if (row.needsReview) return null;
  if (row.verdict !== "Unchanged") return null;

  return (
    <span
      title={
        row.verdictExplanation ??
        "The drawing is unchanged from the approved set. Nothing is printed for this sheet."
      }
      className="mt-1.5 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-sage/40 bg-sage-soft px-2.5 py-0.5 text-[0.74rem] font-medium text-ink-soft"
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-sage-ink" />
      checked · no change
    </span>
  );
}

// The two things the tool cannot settle by itself, stated plainly and with the
// fix attached where there is one.
//
// This is the payoff of the whole rebuild. Nobody can verify forty change notes
// by reading forty rows, so they get rubber-stamped — which is fine until the
// one sheet whose change nothing could describe goes out looking examined. So
// the sheets that need a person say so, in their own words, on their own row.
function ReviewNotice({
  row,
  onFixRevision,
}: {
  row: PaaRow;
  onFixRevision: () => void;
}) {
  if (!row.needsReview && !row.revisionLooksStale) return null;

  return (
    <div className="mt-2 space-y-1.5">
      {row.needsReview && (
        <div className="flex items-start gap-2 rounded-lg border border-warn/40 bg-warn-soft/70 px-3 py-2 text-[0.8rem] leading-relaxed text-ink-soft">
          <span aria-hidden className="mt-px shrink-0 text-[0.9rem]">
            ⚠️
          </span>
          <span className="min-w-0">
            <span className="font-medium text-ink">Needs your eye. </span>
            {row.reviewReason}
          </span>
        </div>
      )}

      {row.revisionLooksStale && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-rule-strong bg-canvas-2 px-3 py-2 text-[0.8rem] leading-relaxed text-ink-soft">
          <span className="min-w-0 flex-1">
            <span className="font-medium text-ink">
              The printed revision didn’t move.{" "}
            </span>
            This sheet’s content changed but its title block still says{" "}
            <span className="font-mono">{row.revision}</span>, so the form would
            list it with the wrong action and revision. Fix it here or on the
            drawing.
          </span>
          <Button
            intent="soft"
            size="sm"
            onClick={onFixRevision}
            className="shrink-0"
          >
            Set S · {formatRevision(parseRevisionMinor(row.revision) + 1)}
          </Button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Download step
// =============================================================================

function DownloadStep({
  url,
  filename,
  onReset,
  approvedFileName,
  onApprovedFile,
  onCombine,
  canCombine,
  combining,
  combined,
}: {
  url: string;
  filename: string;
  onReset: () => void;
  approvedFileName: string | null;
  onApprovedFile: (file: File) => void;
  onCombine: () => void;
  canCombine: boolean;
  combining: boolean;
  /** The combined set, once built. Both outputs of the run end up on this
   *  screen, which is where an operator comes back looking for them. */
  combined: { url: string; name: string } | null;
}) {
  // The approved set can be chosen here too: an operator who skipped the
  // comparison never uploaded one, and "go back and start over" would be the
  // only alternative on the one screen where there is no going back.
  const approved = usePlanPdfDrop(onApprovedFile, combining);

  return (
    <div className="space-y-5">
    <Card>
      <CardBody className="text-center py-12 space-y-6">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sage-soft text-sage-ink">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M5 12l4 4L19 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-[1.5rem] font-medium text-ink">PAA AI1 ready</h2>
          <p className="mt-1 text-[0.95rem] text-ink-soft">{filename}</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <a href={url} download={filename}>
            <Button intent="primary">Download PDF</Button>
          </a>
          <Button intent="ghost" onClick={onReset}>
            Start another
          </Button>
        </div>
      </CardBody>
    </Card>

    {/* The other half of the filing. A button, never automatic: which sheets
        replace which is settled, but where a NEW sheet belongs is not, and that
        is a question for a person. */}
    <Card>
      <CardBody
        {...approved.dropProps}
        className={cn(
          "flex flex-wrap items-center justify-between gap-4 py-5 transition-colors",
          approved.dragging && "rounded-lg bg-primary-soft/60",
        )}
      >
        <div className="min-w-0">
          <div className="text-[1.05rem] font-medium text-ink">Combined plan set</div>
          <div className="mt-1 max-w-xl text-[0.88rem] leading-relaxed text-ink-soft">
            {approved.dragging
              ? "Drop the approved PDF here"
              : combined
                ? combined.name
                : canCombine
                  ? `The approved set with these sheets swapped in, the omitted ones taken out and the new ones inserted. Using ${approvedFileName}.`
                  : "Drop the approved set here (or choose it) to build the approved set with these sheets swapped in and the new ones inserted."}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {combined && (
            <a href={combined.url} download={combined.name}>
              <Button intent="sage">Download plan set</Button>
            </a>
          )}
          <label
            className={cn(
              "cursor-pointer rounded-md border border-rule-strong px-3 py-2 text-[0.85rem] font-medium text-ink transition-colors hover:border-primary hover:bg-primary-soft/40",
              combining && "pointer-events-none opacity-60",
            )}
          >
            <input {...approved.inputProps} />
            {approvedFileName ? "Change" : "Choose approved PDF"}
          </label>
          <Button
            intent={combined ? "secondary" : "primary"}
            onClick={onCombine}
            disabled={!canCombine || combining}
            title={canCombine ? undefined : "Choose the approved set first"}
          >
            {combining
              ? "Working…"
              : combined
                ? "Change the order"
                : "Combine plan sets →"}
          </Button>
        </div>
      </CardBody>
    </Card>
    </div>
  );
}

// Refresh / re-compare glyph. Stroked SVG so it sits on the text baseline and
// scales cleanly — the unicode ↻ rendered as a heavy, misaligned blob.
function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}

// Two panels side by side — "show me both drawings".
function CompareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="7" height="16" rx="1" />
      <rect x="14" y="4" width="7" height="16" rx="1" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

